import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { PlatformProvider } from "@/lib/platform";
import useRpmHeartbeat from "@/hooks/useRpmHeartbeat";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Calculators from "@/pages/Calculators";
import Diet from "@/pages/Diet";
import Medications from "@/pages/Medications";
import DigitalTwin from "@/pages/DigitalTwin";
import Assistant from "@/pages/Assistant";
import DoctorSummary from "@/pages/DoctorSummary";
import LabTracker from "@/pages/LabTracker";
import WeightTracker from "@/pages/WeightTracker";
import AdvancedCarePlanning from "@/pages/AdvancedCarePlanning";
import LifePath from "@/pages/LifePath";
import ProviderInbox from "@/pages/ProviderInbox";
import PatientCommandCenter from "@/pages/PatientCommandCenter";
import AddPatientWizard from "@/pages/AddPatientWizard";
import AuditTrail from "@/pages/AuditTrail";
import Documents from "@/pages/Documents";
import SecuritySettings from "@/pages/SecuritySettings";
import Cardiology from "@/pages/Cardiology";
import Neurology from "@/pages/Neurology";
import Oncology from "@/pages/Oncology";
import Endocrinology from "@/pages/Endocrinology";
import CareTeamHome from "@/pages/CareTeamHome";
import Pulmonology from "@/pages/Pulmonology";
import Gastroenterology from "@/pages/Gastroenterology";
import Rheumatology from "@/pages/Rheumatology";
import InfectiousDisease from "@/pages/InfectiousDisease";
import GeneralHealth from "@/pages/GeneralHealth";
import { EncountersPage, EncounterWorkspace } from "@/components/encounter/SymptomIntakeCard";
import EncounterDetail from "@/pages/EncounterDetail";
import Standards from "@/pages/Standards";
import ConsentGate from "@/components/ConsentGate";
import Admin from "@/pages/Admin";
import CarePlanning from "@/pages/CarePlanning";
import GlobalCommandPalette from "@/components/CommandPalette";

function RequireAuth({ children, allowRoles }) {
  const { user, loading, viewingPatient } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#080A09] text-white flex items-center justify-center">
        <div className="text-[#A3ACA7]">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (allowRoles && !allowRoles.includes(user.role)) {
    return <Navigate to={user.role === "provider" ? "/provider" : "/"} replace />;
  }
  // Routes that a provider can open WITHOUT having selected a viewing patient.
  // Pure calculator/reference pages: no patient context required. Specialty
  // pages open with empty data and let the provider pick a patient after.
  const PROVIDER_STANDALONE = new Set(["/calculators", "/general-health"]);
  const isPatientScopedRoute =
    !location.pathname.startsWith("/provider") &&
    location.pathname !== "/login" &&
    !PROVIDER_STANDALONE.has(location.pathname) &&
    // Also let specialty landings open — the pages themselves render an
    // empty state when there's no viewingPatient.
    !/^\/(cardiology|neurology|oncology|endocrinology|pulmonology|gastroenterology|rheumatology|infectious-disease|nephrology|encounters)(\?|$|\/)/.test(location.pathname);
  if (user.role === "provider" && isPatientScopedRoute && !viewingPatient) {
    return <Navigate to="/provider" replace />;
  }
  return children;
}

function PatientShell({ children }) {
  useRpmHeartbeat();
  const [consentDone, setConsentDone] = React.useState(false);
  return (
    <>
      {!consentDone && <ConsentGate onDone={() => setConsentDone(true)} />}
      <Layout>{children}</Layout>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <PlatformProvider>
        <AuthProvider>
        <BrowserRouter>
          <GlobalCommandPalette />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<Admin />} />
            <Route
              path="/provider"
              element={
                <RequireAuth allowRoles={["provider"]}>
                  <PatientCommandCenter />
                </RequireAuth>
              }
            />
            <Route
              path="/provider/legacy"
              element={
                <RequireAuth allowRoles={["provider"]}>
                  <ProviderInbox />
                </RequireAuth>
              }
            />
            <Route
              path="/provider/add-patient"
              element={
                <RequireAuth allowRoles={["provider"]}>
                  <AddPatientWizard />
                </RequireAuth>
              }
            />
            <Route
              path="/*"
              element={
                <RequireAuth>
                  <PatientShell>
                    <Routes>
                      <Route path="/" element={<CareTeamHome />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/labs" element={<LabTracker />} />
                      <Route path="/weight" element={<WeightTracker />} />
                      <Route path="/care-plan" element={<AdvancedCarePlanning />} />
                      <Route path="/care-planning" element={<CarePlanning />} />
                      <Route path="/lifepath" element={<LifePath />} />
                      <Route path="/calculators" element={<Calculators />} />
                      <Route path="/diet" element={<Diet />} />
                      <Route path="/medications" element={<Medications />} />
                      <Route path="/twin" element={<DigitalTwin />} />
                      <Route path="/assistant" element={<Assistant />} />
                      <Route path="/summary" element={<DoctorSummary />} />
                      <Route path="/audit" element={<AuditTrail />} />
                      <Route path="/documents" element={<Documents />} />
                      <Route path="/security" element={<SecuritySettings />} />
                      <Route path="/cardiology" element={<Cardiology />} />
                      <Route path="/neurology" element={<Neurology />} />
                      <Route path="/oncology" element={<Oncology />} />
                      <Route path="/endocrinology" element={<Endocrinology />} />
                      <Route path="/pulmonology" element={<Pulmonology />} />
                      <Route path="/gastroenterology" element={<Gastroenterology />} />
                      <Route path="/rheumatology" element={<Rheumatology />} />
                      <Route path="/infectious-disease" element={<InfectiousDisease />} />
                      <Route path="/general-health" element={<GeneralHealth />} />
                      <Route path="/encounters" element={<EncountersPage />} />
                      <Route path="/encounters/:id" element={<EncounterDetail />} />
                      <Route path="/standards" element={<Standards />} />
                    </Routes>
                  </PatientShell>
                </RequireAuth>
              }
            />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </PlatformProvider>
      </ThemeProvider>
      <Toaster theme="system" position="top-right" richColors />
    </div>
  );
}

export default App;
